import { containerStatusPresentation } from './container-status-presentation.mjs?v=20260806.100';
import { columnEditableForRole, fieldOptions } from './container-field-registry.mjs?v=20260806.100';
import {
  TERMINAL_FEES_HOLD_RELEASE,
  pairingIsReportedEmpty,
  pairingReleaseBlocker,
} from './empty-pairing-flow.mjs?v=20260806.100';
import { containerNumberEditable, rowUsesCustomerOwnChassis } from './container-field-registry.mjs?v=20260806.100';
import {
  dashboardEditorInputSpec,
  dashboardEditorValue,
} from './dashboard-cell-interactions.mjs?v=20260806.100';
import { normalizeDashboardDrivers } from './dashboard-driver-dropdown.mjs?v=20260806.100';
import { APPEND_ONLY_NOTE_FIELDS } from './dashboard-field-autosave.mjs?v=20260806.100';
import { CUSTOM_ADDRESS_VALUE, isCustomAddress } from './address-catalog.mjs?v=20260806.100';
import { deliveryOrderLink, deliveryOrderObjectPath } from './delivery-order-upload.mjs?v=20260806.100';

const EM_DASH = '—';
const FINANCE_ROLES = new Set(['admin', 'finance']);
const EXPENSE_COST_FIELDS = new Set([
  'pickup_cost_cents',
  'return_cost_cents',
  'transfer_cost_cents',
  'exception_fee_cents',
  'special_fee_amount_cents',
  'special_fee_2_amount_cents',
  'special_fee_3_amount_cents',
  'pier_pass_cents',
  'total_expense_cents',
]);
const CUSTOMER_REVENUE_FIELDS = new Set([
  'customer_special_fee_cents',
  'service_fee_cents',
]);
const EMPTY_EXPENSE_VALUES = new Set(['Not provided', EM_DASH, '$0.00']);

const FIELD_GROUPS = Object.freeze({
  notes: Object.freeze([
    'hold_status',
    'hold_note',
    'customer_note',
    'special_fee_detail',
    'special_fee_2_detail',
    'special_fee_3_detail',
    'customer_special_fee_detail',
  ]),
});

// Fields are grouped by the container's lifecycle, never by field type or registry order: the
// operator's eye follows the box through its actual journey, so everything about one step lives
// in one place. Pickup appointment, pickup driver and pickup cost belong together even though
// they are a date, a name and an amount — grouping them by type is what sent operators hunting
// through three sections to answer one question.
const AUTHORIZED_FIELD_SECTIONS = Object.freeze([
  Object.freeze({
    key: 'identity',
    label: 'Identification',
    fields: Object.freeze([
      'container_no',
      'master_bl_no',
      'pin',
      'size_type',
      'weight_lbs',
    ]),
  }),
  Object.freeze({
    key: 'customer',
    label: 'Customer',
    fields: Object.freeze([
      'customer_name',
      'customer_paid',
      'customer_quote_cents',
      'customer_receivable_cents',
      'delivery_warehouse_address',
    ]),
  }),
  Object.freeze({
    key: 'ocean',
    label: 'Ocean & Terminal',
    fields: Object.freeze([
      'ssl',
      'terminal_name',
      'ocean_status',
      'pre_alert_status',
      'do_ref',
      'eta_at',
      'lfd_at',
      'hold_status',
      'hold_note',
      'customer_note',
    ]),
  }),
  Object.freeze({
    key: 'pickup',
    label: 'Pickup',
    fields: Object.freeze([
      'pickup_appointment_at',
      'pickup_driver_name',
      'pickup_cost_cents',
      'chassis_no',
      'customer_chassis',
      'pickup_company',
    ]),
  }),
  // Between Pickup and Empty Return, because that is the journey: the box is
  // moved to the second yard before the empty is taken back. Same order as
  // RELEASE_LEG_FIELDS below and as LEGS in functions/src/dispatch-release.ts.
  Object.freeze({
    key: 'transfer',
    label: 'Transfer',
    fields: Object.freeze([
      'transfer_driver_name',
      'transfer_at',
      'transfer_from_address',
      'transfer_to_address',
      'transfer_cost_cents',
    ]),
  }),
  Object.freeze({
    key: 'empty_return',
    label: 'Empty Return',
    fields: Object.freeze([
      'empty_from_address',
      'empty_to_address',
      'empty_container_no',
      'paired_empty_from_address',
      'paired_empty_to_address',
      'return_driver_name',
      'return_cost_cents',
      'return_empty_lfd_at',
      'reported_empty_at',
      'reported_empty_place',
      'actual_empty_returned_at',
      'empty_returned_at',
    ]),
  }),
  Object.freeze({
    key: 'expenses',
    label: 'Expense Summary',
    fields: Object.freeze([
      'ctf_status',
      'tmf_status',
      'ctf_tmf_status',
      'exception_fee_cents',
      'service_fee_cents',
      'pier_pass_cents',
      'special_fee_name',
      'special_fee_date',
      'special_fee_amount_cents',
      'special_fee_detail',
      'special_fee_2_name',
      'special_fee_2_date',
      'special_fee_2_amount_cents',
      'special_fee_2_detail',
      'special_fee_3_name',
      'special_fee_3_date',
      'special_fee_3_amount_cents',
      'special_fee_3_detail',
      'customer_special_fee_cents',
      'customer_special_fee_detail',
      'total_expense_cents',
      'profit_cents',
      'remittance_status',
    ]),
  }),
]);

const AUTHORIZED_OTHER_SECTION = Object.freeze({ key: 'other', label: 'Other Authorized Fields' });

// Expenses keep their semantic colours; only the reading order and the group headings are fixed.
const EXPENSE_GROUPS = Object.freeze([
  Object.freeze({
    key: 'operating',
    label: 'Operating Cost',
    fields: Object.freeze([
      'pickup_cost_cents',
      'return_cost_cents',
      'transfer_cost_cents',
      'exception_fee_cents',
    ]),
  }),
  Object.freeze({
    key: 'special',
    label: 'Special Fees',
    fields: Object.freeze([
      'special_fee_amount_cents',
      'special_fee_2_amount_cents',
      'special_fee_3_amount_cents',
      'customer_special_fee_cents',
    ]),
  }),
  Object.freeze({
    key: 'pass-through',
    label: 'Pass-Through & Totals',
    fields: Object.freeze([
      'service_fee_cents',
      'pier_pass_cents',
      'total_expense_cents',
    ]),
  }),
  Object.freeze({
    key: 'customer',
    label: 'Customer Side',
    fields: Object.freeze([
      'customer_quote_cents',
      'customer_receivable_cents',
      'profit_cents',
      'remittance_status',
      'ctf_tmf_status',
    ]),
  }),
]);

const TIMELINE_DEFINITIONS = Object.freeze([
  {
    key: 'created',
    label: 'Task Created',
    fields: ['created_at'],
    icon: 'ph-calendar-blank',
  },
  {
    key: 'scheduled',
    label: 'Pickup Scheduled',
    fields: ['pickup_appointment_at', 'delivery_appointment_at', 'eta_at'],
    icon: 'ph-calendar-check',
  },
  {
    // Releasing is a milestone, not a field edit: it is the moment the work left the
    // desk (Alan, 2026-08-03).
    key: 'released',
    label: 'Released to Drivers',
    fields: ['dispatch_released_at'],
    icon: 'ph-paper-plane-tilt',
  },
  {
    key: 'picked_up',
    label: 'Container Picked Up',
    fields: ['picked_up_at', 'load_out_at'],
    icon: 'ph-truck',
  },
  {
    key: 'delivered',
    label: 'Loaded Delivery Completed',
    fields: ['loaded_delivered_at', 'warehouse_arrived_at'],
    icon: 'ph-check-circle',
  },
  {
    key: 'empty_ready',
    label: 'Empty Container Ready',
    fields: ['reported_empty_at', 'reported_empty_place', 'empty_ready_at'],
    icon: 'ph-package',
  },
  {
    key: 'empty_picked_up',
    label: 'Empty Return Started',
    fields: ['empty_picked_up_at'],
    icon: 'ph-truck-trailer',
  },
  {
    key: 'empty_returned',
    label: 'Empty Container Returned',
    fields: ['actual_empty_returned_at', 'empty_returned_at'],
    icon: 'ph-check-circle',
  },
]);

const RELATED_LABELS = Object.freeze({
  account_number: 'Account Number',
  name: 'Name',
  address: 'Address',
  delivery_notes: 'Delivery Notes',
  phone: 'Phone',
  wechat: 'WeChat',
  truck: 'Truck',
  driver_code: 'Driver Number',
  real_email: 'Email',
  account_login_email: 'Account Login',
  note: 'Note',
});

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function roleToken(value) {
  return String(value || '').trim().toLowerCase();
}

function cleanText(value) {
  return String(value ?? '')
    .replaceAll('â€”', '—')
    .replaceAll('Â·', '·')
    .trim();
}

function initials(value) {
  const parts = cleanText(value).split(/\s+/).filter(Boolean);
  return (parts.length > 1
    ? `${parts[0][0]}${parts.at(-1)[0]}`
    : (parts[0] || 'D').slice(0, 2)).toUpperCase();
}

function isPresent(value) {
  return value !== null && value !== undefined && value !== '';
}

function asDate(value) {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) && value.getTime() > 0 ? value : null;
  }
  if (value && typeof value.toDate === 'function') {
    const date = value.toDate();
    return date instanceof Date && Number.isFinite(date.getTime()) && date.getTime() > 0 ? date : null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    const date = new Date(Number(value));
    return Number.isFinite(date.getTime()) && date.getTime() > 0 ? date : null;
  }
  if (typeof value === 'string' && value.trim()) {
    const date = new Date(value);
    return Number.isFinite(date.getTime()) && date.getTime() > 0 ? date : null;
  }
  return null;
}

const DATE_PART_FORMAT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

// 24-hour everywhere (Alan, 2026-08-11).
const TIME_PART_FORMAT = new Intl.DateTimeFormat('en-US', {
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

function isDateColumn(column) {
  return column?.format === 'date-only'
    || column?.format === 'date-time'
    || column?.editor === 'date'
    || column?.editor === 'actual_empty_return';
}

function isAddressColumn(column) {
  return column?.interaction === 'address'
    || column?.editor === 'address'
    || /_address$/.test(String(column?.key || ''));
}

// Same rule as the Dashboard table: the date always shows, the time only when the value carries
// one, and an em dash stands in for an empty value.
function formattedDate(value, { dateOnly = false } = {}) {
  const date = asDate(value);
  if (!date) return EM_DASH;
  const carriesTime = !dateOnly && (date.getHours() !== 0 || date.getMinutes() !== 0);
  return carriesTime
    ? `${DATE_PART_FORMAT.format(date)} ${TIME_PART_FORMAT.format(date)}`
    : DATE_PART_FORMAT.format(date);
}

function taskDateText(key, row, column) {
  const dateOnly = column?.format === 'date-only'
    || (key === 'actual_empty_returned_at' && row?.actual_empty_returned_precision !== 'date_time');
  return formattedDate(row?.[key], { dateOnly });
}

function customAddressChip(column, row, addressCatalog) {
  if (!addressCatalog || !isAddressColumn(column)) return null;
  try {
    return isCustomAddress(addressCatalog, cleanText(row?.[column.key])) ? 'Custom' : null;
  } catch {
    return null;
  }
}

function humanize(value) {
  const text = cleanText(value).replaceAll('_', ' ').toLowerCase();
  return text ? text.replace(/\b\w/g, (letter) => letter.toUpperCase()) : 'Not provided';
}

function mergedRoleSafeRow(row, detail, role) {
  const summary = detail?.summary && typeof detail.summary === 'object' ? detail.summary : {};
  const visible = detail?.visible_fields && typeof detail.visible_fields === 'object'
    ? detail.visible_fields : {};
  const finance = FINANCE_ROLES.has(roleToken(role))
    && detail?.finance && typeof detail.finance === 'object' ? detail.finance : {};
  return {
    ...(row || {}),
    ...visible,
    ...finance,
    // Carried explicitly rather than spread from `visible`, because it is a
    // list of leg records and not a field value - the timeline reads it.
    driver_progress: Array.isArray(detail?.driver_progress) ? detail.driver_progress : [],
    ...summary,
    id: row?.id || detail?.container_id || '',
    container_no: summary.container_no || row?.container_no || detail?.container_id || 'Container',
  };
}

function columnMap(columns) {
  return new Map((Array.isArray(columns) ? columns : []).map((column) => [column.key, column]));
}

// The DO is a link now (Alan, 2026-08-02), so a value that is one is rendered as a
// link rather than as text you have to copy by hand. Restricted to http and https
// on purpose: a stored "javascript:" or "data:" value must never become a live
// anchor, and everything still goes through escapeHtml either way.
function valueMarkup(value) {
  const text = String(value ?? '');
  if (!/^https?:\/\/\S+$/i.test(text)) return escapeHtml(text);
  return `<a href="${escapeHtml(text)}" target="_blank" rel="noopener noreferrer">${escapeHtml(text)}</a>`;
}

function displayValue(key, row, columnsByKey, formatValue) {
  const column = columnsByKey.get(key) || { key, label: key };
  if (isDateColumn(column)) return taskDateText(key, row, column);
  if (!isPresent(row?.[key])) return 'Not provided';
  try {
    const formatted = typeof formatValue === 'function' ? formatValue(key, row, column) : row[key];
    const text = cleanText(formatted);
    return text && text !== '—' ? text : 'Not provided';
  } catch {
    return cleanText(row[key]) || 'Not provided';
  }
}

function visibleEntries(keys, row, columnsByKey, formatValue, {
  includeEmpty = false, addressCatalog = null,
} = {}) {
  return keys.flatMap((key) => {
    const column = columnsByKey.get(key);
    if (!column) return [];
    const value = displayValue(key, row, columnsByKey, formatValue);
    if (!includeEmpty && (value === 'Not provided' || value === EM_DASH)) return [];
    return [{
      key,
      label: column.label || column.headerLabel || humanize(key),
      value,
      chip: customAddressChip(column, row, addressCatalog),
    }];
  });
}

function customChipMarkup(label) {
  return label
    ? `<span class="container-task-modal__custom-chip">${escapeHtml(label)}</span>`
    : '';
}

function definitionList(entries, className = '') {
  if (!entries.length) {
    return '<p class="container-task-modal__empty">No information is available yet.</p>';
  }
  return `<dl class="container-task-modal__definition-list ${escapeHtml(className)}">${entries.map((entry) => `
    <div${entry.tone ? ` class="container-task-modal__expense-item container-task-modal__expense-item--${escapeHtml(entry.tone)}"` : ''}>
      <dt>${escapeHtml(entry.label)}</dt>
      <dd>${valueMarkup(entry.value)}${customChipMarkup(entry.chip)}</dd>
    </div>`).join('')}
  </dl>`;
}

function groupHeading(label) {
  return `<h4 class="container-task-modal__group-heading">${escapeHtml(label)}</h4>`;
}

function card(title, body, className = '') {
  return `<section class="container-task-modal__card ${escapeHtml(className)}">
    <h3>${escapeHtml(title)}</h3>
    ${body}
  </section>`;
}

export function containerTaskModalSections(role) {
  const sections = [
    { key: 'overview', label: 'Overview' },
    { key: 'pod', label: 'POD' },
    { key: 'notes', label: 'Notes' },
  ];
  if (FINANCE_ROLES.has(roleToken(role))) sections.push({ key: 'expenses', label: 'Expenses' });
  return sections;
}

function syntheticFinanceColumns(column) {
  if (column?.key !== 'ctf_tmf_status' || column?.editor !== 'ctf_tmf') return [column];
  return [
    {
      key: 'ctf_status',
      label: 'CTF',
      editor: 'select',
      options: ['paid', 'unpaid'],
      editRoles: ['finance'],
    },
    {
      key: 'tmf_status',
      label: 'TMF',
      editor: 'select',
      options: ['paid', 'unpaid'],
      editRoles: ['finance'],
    },
    {
      key: 'customer_paid',
      label: 'Customer Payment',
      editor: 'select',
      options: ['customer_paid'],
      editRoles: ['finance'],
    },
  ];
}

export function containerTaskModalEditableColumns(columns, role) {
  return (Array.isArray(columns) ? columns : [])
    .filter((column) => columnEditableForRole(column, role))
    .flatMap(syntheticFinanceColumns);
}

// Grouping only reorders what the caller already resolved for this role: a column that is not in
// the incoming list can never appear, and a section without a single visible column is dropped.
export function containerTaskModalFieldSections(columns) {
  const remaining = new Map((Array.isArray(columns) ? columns : [])
    .filter((column) => column && typeof column.key === 'string')
    .map((column) => [column.key, column]));
  const sections = AUTHORIZED_FIELD_SECTIONS.flatMap((section) => {
    const grouped = section.fields.flatMap((key) => {
      const column = remaining.get(key);
      if (!column) return [];
      remaining.delete(key);
      return [column];
    });
    return grouped.length
      ? [{ key: section.key, label: section.label, columns: grouped }]
      : [];
  });
  const leftovers = [...remaining.values()];
  return leftovers.length
    ? [...sections, { ...AUTHORIZED_OTHER_SECTION, columns: leftovers }]
    : sections;
}

// The finance editor splits one stored field into three controls. When those controls are on
// screen the stored field must not also be shown, or the same value appears twice in one group.
const SYNTHETIC_PARENT_BY_KEY = Object.freeze({
  ctf_status: 'ctf_tmf_status',
  tmf_status: 'ctf_tmf_status',
  customer_paid: 'ctf_tmf_status',
});

// Callers already hand this modal the columns for the current role. Re-checking the registry's own
// roles list here costs nothing and keeps a grouping change from ever being the thing that widens
// what a role can see: a group can only reorder fields, never admit one.
function columnVisibleForRole(column, role) {
  if (!Array.isArray(column?.roles)) return true;
  const normalized = roleToken(role);
  return normalized === 'admin' || column.roles.includes(normalized);
}

// Within a group the read-only facts come first and the editable fields follow, always in that
// order, so the band an operator can act on sits in the same relative place in every group.
// The split is computed per role because the same field is editable for one role and not another
// (pickup_cost_cents is editable for finance, read-only for dispatch) — it can never be hardcoded.
export function containerTaskModalLifecycleGroups({
  row = {},
  columnsByKey = new Map(),
  role = '',
  formatValue = (key, source) => source[key],
  addressCatalog = null,
  editMode = false,
  editableColumns = [],
} = {}) {
  const editableByKey = new Map((Array.isArray(editableColumns) ? editableColumns : [])
    .filter((column) => column && typeof column.key === 'string')
    .map((column) => [column.key, column]));
  const suppressed = new Set();
  if (editMode) {
    editableByKey.forEach((_column, key) => {
      const parent = SYNTHETIC_PARENT_BY_KEY[key];
      if (parent) suppressed.add(parent);
    });
  }
  return AUTHORIZED_FIELD_SECTIONS.flatMap((section) => {
    // Reading, the finance roll-up is the Expenses card below: it already groups these fields and
    // colours them. Repeating all of it here doubled the page height on a fee-heavy container.
    // Editing still needs the group, because that is where those fields are changed.
    if (section.key === 'expenses' && !editMode) return [];
    const readonly = [];
    const editable = [];
    section.fields.forEach((key) => {
      if (suppressed.has(key)) return;
      const editableColumn = editMode ? editableByKey.get(key) : null;
      if (editableColumn) {
        editable.push({
          key,
          label: editableColumn.label || humanize(key),
          column: editableColumn,
          chip: customAddressChip(columnsByKey.get(key), row, addressCatalog),
        });
        return;
      }
      const column = columnsByKey.get(key);
      if (!column || !columnVisibleForRole(column, role)) return;
      const [entry] = visibleEntries([key], row, columnsByKey, formatValue, { addressCatalog });
      if (!entry) return;
      (columnEditableForRole(column, role)
        && !(key === 'container_no' && !containerNumberEditable(row, role)) ? editable : readonly).push(entry);
    });
    return readonly.length || editable.length
      ? [{
        key: section.key, label: section.label, readonly, editable,
      }]
      : [];
  });
}

export function containerTaskModalExpenseGroups(row, columnsByKey, formatValue) {
  return EXPENSE_GROUPS.flatMap((group) => {
    const entries = visibleEntries(group.fields, row, columnsByKey, formatValue, {
      includeEmpty: true,
    }).map((entry) => ({
      ...entry,
      tone: entry.key === 'profit_cents'
        ? 'profit'
        : CUSTOMER_REVENUE_FIELDS.has(entry.key)
          ? 'customer-revenue'
          : EXPENSE_COST_FIELDS.has(entry.key) ? 'cost' : 'neutral',
    }));
    return entries.length
      ? [{
        key: group.key,
        label: group.label,
        entries,
        // A group is empty only when every field in it is blank. Collapsing per field instead
        // would leave a ragged column that is harder to scan than the full list.
        isEmpty: entries.every(({ value }) => EMPTY_EXPENSE_VALUES.has(value)),
      }]
      : [];
  });
}

// The instants a driver stamps on their own leg, in the order they happen. Every
// one of these is already recorded on the dispatch_task; the modal simply never
// read them, so a driver could work all day and the container's timeline showed
// nothing between dispatch and completion.
const DRIVER_PROGRESS_STEPS = Object.freeze([
  ['assigned_at_millis', 'Assigned'],
  ['accepted_at_millis', 'Accepted'],
  ['started_at_millis', 'Started'],
  ['arrived_at_millis', 'Arrived'],
  ['completed_at_millis', 'Completed'],
  ['cancelled_at_millis', 'Cancelled'],
]);

const LEG_LABELS = Object.freeze({
  pickup_from_terminal: 'Pickup',
  load_out_to_customer: 'Delivery',
  return_empty: 'Empty Return',
  transfer: 'Transfer',
});

export function driverProgressTimelineItems(row = {}) {
  const legs = Array.isArray(row?.driver_progress) ? row.driver_progress : [];
  return legs.flatMap((leg) => {
    const legLabel = LEG_LABELS[leg?.leg_type] || cleanText(leg?.leg_type) || 'Leg';
    const who = cleanText(leg?.driver_name);
    return DRIVER_PROGRESS_STEPS.flatMap(([key, step]) => {
      const millis = leg?.[key];
      if (!Number.isFinite(millis)) return [];
      return [{
        key: `${leg.task_id}:${key}`,
        label: who ? `${legLabel} ${step} — ${who}` : `${legLabel} ${step}`,
        field: key,
        // Carried so the rail marks the step with the driver rather than a dot.
        // These entries have no icon of their own, so the marker was an empty
        // circle sitting next to a line that names a person (Alan, 2026-08-05).
        who,
        date: new Date(millis),
      }];
    });
  });
}

export function taskTimelineItems(row = {}) {
  const milestones = TIMELINE_DEFINITIONS.flatMap((definition) => {
    const field = definition.fields.find((candidate) => asDate(row[candidate]));
    const date = field ? asDate(row[field]) : null;
    return date ? [{ ...definition, field, date }] : [];
  });
  // Merged into one chronological list rather than kept in a separate panel:
  // the operator's question is "what has happened to this container", and two
  // lists side by side make them do the interleaving by eye.
  return [...milestones, ...driverProgressTimelineItems(row)]
    .sort((left, right) => left.date - right.date);
}

function relatedDefinitionList(record) {
  const entries = Object.entries(record || {}).flatMap(([key, value]) => {
    if (key === 'id' || !isPresent(value) || !RELATED_LABELS[key]) return [];
    return [{ label: RELATED_LABELS[key], value: cleanText(value) }];
  });
  return definitionList(entries);
}

// The driver's profile is reachable from their initials wherever their name is
// drawn. Matching is by name because that is the only handle the released legs
// and the driver progress entries carry; a name that two active drivers share
// drops the link rather than opening the wrong person's profile.
function driverIdsByName(drivers) {
  const byName = new Map();
  for (const driver of Array.isArray(drivers) ? drivers : []) {
    const name = cleanText(driver?.name).toLowerCase();
    const id = cleanText(driver?.id);
    if (!name || !id) continue;
    byName.set(name, byName.has(name) ? '' : id);
  }
  return byName;
}

function driverAvatarMarkup(name, { className, ids = null, enabled = false } = {}) {
  const label = cleanText(name);
  if (!label) return '';
  const glyph = escapeHtml(initials(label));
  const id = enabled ? cleanText(ids?.get(label.toLowerCase())) : '';
  return id
    ? `<button type="button" class="${className}" data-task-driver-open="${escapeHtml(id)}" aria-label="Open ${escapeHtml(label)} driver profile">${glyph}</button>`
    : `<span class="${className}" aria-hidden="true">${glyph}</span>`;
}

// The three legs as they stood when the work was released, each with the driver who
// took it and how far they have got. Falls back to the live table values before a
// release, so the panel shows who is *about* to be sent as well as who was sent.
// Pickup and Empty Return only, for now (Alan, 2026-08-04). Transfer is a leg
// the operation is not running yet, and a row that is always "Not started"
// teaches people to ignore the panel.
//
// Each leg carries where it runs from and to, and the handful of facts the
// driver on that leg actually needs - the appointment they have to make, the
// equipment they are on, the box they are moving. They live behind the row's
// own disclosure so the panel stays a list of who has what until asked.
// The from/to candidate lists MUST mirror releaseLegRoute in
// functions/src/index.ts key for key: this panel decides when the button is
// live, the callable decides what a press sends, and they read the same
// Container. The pickup list used to check `terminal` while the server checked
// `terminal_name`, so the button could sit disabled over a leg the server was
// ready to send.
const RELEASE_LEG_FIELDS = Object.freeze([
  Object.freeze({
    key: 'pickup',
    label: 'Pickup',
    field: 'pickup_driver_name',
    costField: 'pickup_cost_cents',
    startFields: Object.freeze(['picked_up_at', 'load_out_at']),
    doneFields: Object.freeze(['loaded_delivered_at', 'warehouse_arrived_at']),
    // paired_empty_to_address last: on a paired release the driver picks the load
    // up at the terminal they returned the empty to (Alan, 2026-08-12: "空柜的
    // Empty to address 是重柜的from").
    from: Object.freeze(['pickup_company', 'terminal_name', 'paired_empty_to_address']),
    // transfer_from_address first: a load with a transfer is carried to the yard
    // the transfer starts from, not to the warehouse (Alan, 2026-08-16). Mirrors
    // tryReleaseLegRoute in functions/src/index.ts, which is what the driver
    // actually receives - if these two disagree the panel draws one route and the
    // release sends another.
    to: Object.freeze(['transfer_from_address', 'delivery_warehouse_address']),
    facts: Object.freeze([
      ['Appointment', 'pickup_appointment_at', 'date'],
      ['Container #', 'container_no', 'text'],
      ['Chassis #', 'chassis_no', 'text'],
      ['LFD', 'lfd_at', 'date'],
    ]),
  }),
  // Second, not last: the box is moved to the next yard before the empty goes
  // back, and LEGS in functions/src/dispatch-release.ts numbers the released
  // tasks in this same order. The callable has always released a named transfer;
  // the panel simply never drew it, so a transfer driver was sent by a button
  // that did not show them.
  Object.freeze({
    key: 'transfer',
    label: 'Transfer',
    field: 'transfer_driver_name',
    costField: 'transfer_cost_cents',
    startFields: Object.freeze([]),
    doneFields: Object.freeze(['transfer_at']),
    from: Object.freeze(['transfer_from_address']),
    to: Object.freeze(['transfer_to_address']),
    facts: Object.freeze([
      ['Transfer Date', 'transfer_at', 'date'],
    ]),
  }),
  Object.freeze({
    key: 'empty_return',
    label: 'Empty Return',
    field: 'return_driver_name',
    costField: 'return_cost_cents',
    startFields: Object.freeze(['empty_picked_up_at']),
    doneFields: Object.freeze(['empty_returned_at', 'actual_empty_returned_at']),
    from: Object.freeze(['empty_from_address', 'paired_empty_from_address', 'delivery_warehouse_address']),
    to: Object.freeze(['empty_to_address', 'paired_empty_to_address']),
    // Each leg names the box that leg moves. Pickup moves the laden container, so
    // its Container # is this record's own; Empty Return moves the empty that was
    // paired to it, so its Container # is the Empty # (Alan, 2026-08-05). Calling
    // the second one "Empty #" made the driver on that leg look for a number that
    // was not the one on the box they are taking back.
    facts: Object.freeze([
      ['Container #', 'empty_container_no', 'text'],
      ['Reported Empty', 'reported_empty_at', 'date'],
      ['Return LFD', 'return_empty_lfd_at', 'date'],
      ['Chassis #', 'chassis_no', 'text'],
    ]),
  }),
]);

function firstValue(row, keys) {
  for (const key of keys) {
    const value = cleanText(row?.[key]);
    if (value) return value;
  }
  return '';
}

// An empty that has been reported and not yet stamped as returned - the same
// predicate the pairing picker and the Empty chip count with, imported rather
// than restated so the three cannot drift.
function rowIsReportedEmpty(row) {
  return pairingIsReportedEmpty(row);
}

// Whether some load has already named this empty. The list is computed over the
// whole fleet by the dashboard callable - it cannot be read off this one row,
// because the laden that claimed it is a different Container - so app.js hands it
// in. Unknown means unclaimed, which keeps the release available rather than
// hiding it on a doubt.
let claimedEmptyNumbers = null;

// The empty a load has paired, found by number in the fleet the Dashboard has
// loaded. Handed in by app.js. The Empty Return leg's route lives on THAT row and
// stays there (Alan, 2026-08-16: "重柜是单独的不要把空柜的 empty from 和 empty to
// 同步到重柜表格上") - so the leg is drawn from it rather than from fields
// copied onto the load. Null when nothing was handed in, in which case the leg
// reads the load's own fields as before.
let findPairedEmpty = null;
function pairedEmptyRow(row) {
  const number = String(row?.empty_container_no ?? '').trim();
  if (!number || typeof findPairedEmpty !== 'function') return null;
  return findPairedEmpty(number) ?? null;
}

function rowEmptyIsClaimed(row) {
  if (!(claimedEmptyNumbers instanceof Set) || !claimedEmptyNumbers.size) return false;
  return claimedEmptyNumbers.has(String(row?.container_no ?? '').trim().toUpperCase());
}

/**
 * The legs an earlier press already sent.
 *
 * Two shapes, because the dashboard projection carries no arrays: the Container
 * document holds a list of objects, and the row that reaches this panel holds
 * "pickup=George,empty_return=Guo" - the same facts flattened to a string so they
 * survive sanitizeRoleContainerDashboardProjection (2026-08-19). Before that the
 * field arrived as null and every leg read as unsent, so a partially released
 * Container showed "Waiting to release" over work already on a driver's phone.
 */
function releasedLegEntries(value) {
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string' || !value.trim()) return null;
  return value.split(',').map((part) => {
    const [leg, driver] = part.split('=');
    return { leg: String(leg ?? '').trim(), driver_name: String(driver ?? '').trim() };
  }).filter(({ leg }) => leg);
}

export function dispatchReleaseLegs(row = {}) {
  const released = releasedLegEntries(row.dispatch_released_legs);
  // A Container released under the old all-or-nothing shape carries the flag and
  // no legs array. That release sent everything named, so the flag alone means
  // every named leg is out - the same legacy reading the callable applies.
  const legacyReleasedAll = !released
    && (row.dispatch_released === true || Boolean(asDate(row.dispatch_released_at)));
  // A reported empty is only ever the empty half of a trip (Alan, 2026-08-21:
  // "点进去空柜的tab那么就只应该显示空柜的部分派送的").
  //
  // A Container is one document across its whole life: it arrives laden, gets
  // delivered, and becomes the empty waiting to go back. So an empty still
  // carries the pickup driver and appointment from the leg that is already
  // finished, and the panel drew a Pickup leg for a trip nobody is going to make.
  // Worse than noise: CAAU6850464 has no pickup origin left, so that dead leg
  // reported "Pickup: From missing" and DISABLED the release of the empty return
  // that was the only real work on the row.
  //
  // Three of the 78 reported empties in production carry a pickup driver, so this
  // hides a leg on three rows and unblocks one. The predicate is the Empty chip's
  // own, imported rather than restated.
  const emptyOnly = rowIsReportedEmpty(row);
  return RELEASE_LEG_FIELDS.flatMap((leg) => {
    if (emptyOnly && leg.key !== 'empty_return') return [];
    const releasedEntry = released
      ? released.find((entry) => entry?.leg === leg.key || entry?.label === leg.label)
      : null;
    const releasedName = cleanText(releasedEntry?.driver_name);
    // The Empty Return leg reads the paired empty, driver as well as route
    // (Alan, 2026-08-16: "空柜的return driver 显示的同时也是空柜的 empty return
    // driver 在tab里面"). Since the pairing stopped writing the load's own Return
    // Driver - that is a later trip, and his rule - the only record of who takes
    // the empty back is the empty's own row, so this leg said "Unassigned" about
    // a run that already had a driver on it.
    const legEmpty = leg.key === 'empty_return' ? pairedEmptyRow(row) : null;
    const ownName = cleanText(row[leg.field]);
    const emptyName = legEmpty ? cleanText(legEmpty.return_driver_name) : '';
    // ONE driver for the pair, and this leg shows whichever side of it has a name
    // (Alan, 2026-08-17: "你看见这个container#了吗对应的应该就是driver名字对应着这个
    // 柜号也就是task的信息").
    //
    // Reading the empty's own Return Driver was not enough at the moment it
    // matters most. During a pairing the empty has not been written yet - the name
    // in its Return Driver column is the mirror the release is ABOUT to store -
    // so the leg said "Unassigned" beside an Empty # and a route it was drawing
    // from that same pairing. The load's Pickup Driver is that person, by his rule
    // of an hour earlier that the two are one, so it is the last fallback.
    //
    // Gated on there being a pairing at all: without an Empty # this leg is not
    // about anyone yet, and showing the pickup driver would invent a return trip.
    const paired = leg.key === 'empty_return' && Boolean(cleanText(row.empty_container_no))
      ? emptyName || cleanText(row.pickup_driver_name)
      : '';
    const name = releasedName || ownName || paired;
    // ...and where that name came from, because it changes what the row means:
    // a driver on the EMPTY's record is dispatched by the empty's own 还空, not
    // by this Container's release. Saying "Waiting to release" over it would
    // promise a send this button does not make.
    const nameFromPairedEmpty = !releasedName && !ownName && Boolean(paired);
    // A leg with no driver is normally not a leg at all - there is nothing to
    // show and nothing to send. The Empty Return is the exception the moment a
    // pairing names the empty: the section has to be on screen BEFORE the release
    // so its route can be read and corrected (Alan, 2026-08-16: "当和空柜配对后在
    // release之前下面应该有empty return部分"). The driver arrives with the pairing,
    // which is written just before the dispatch, so waiting for it means the
    // operator never sees this leg while there is still time to fix it.
    const pairedEmpty = leg.key === 'empty_return' && Boolean(cleanText(row.empty_container_no));
    if (!name && !pairedEmpty) return [];
    const done = leg.doneFields.some((candidate) => asDate(row[candidate]));
    const started = done || leg.startFields.some((candidate) => asDate(row[candidate]));
    // The Empty Return moves the paired empty, so its route is that empty's Empty
    // From/To when the pairing is known. The load's own fields are only the
    // fallback - a released load carries them as paired_empty_* from the handoff.
    const routeFrom = legEmpty ? cleanText(legEmpty.empty_from_address) : '';
    const routeTo = legEmpty ? cleanText(legEmpty.empty_to_address) : '';
    return [{
      key: leg.key,
      label: leg.label,
      name,
      nameFromPairedEmpty,
      // Sent by an earlier press. What is not sent can still be sent by the next
      // one - since 2026-08-12 a release moves leg by leg, not all at once.
      sent: legacyReleasedAll || Boolean(releasedEntry),
      progress: done ? 'Completed' : started ? 'In progress' : 'Not started',
      state: done ? 'complete' : started ? 'active' : 'waiting',
      costField: leg.costField,
      cost: Number.isSafeInteger(row?.[leg.costField]) ? row[leg.costField] : null,
      from: routeFrom || firstValue(row, leg.from),
      to: routeTo || firstValue(row, leg.to),
      facts: leg.facts.flatMap(([factLabel, key, kind]) => {
        const raw = row?.[key];
        const value = kind === 'date'
          ? (asDate(raw) ? formattedDate(asDate(raw)) : '')
          : cleanText(raw);
        return value ? [{ label: factLabel, value }] : [];
      }),
    }];
  });
}

// A driver sent without both ends of the run has to phone in and ask where they
// are going, which is the failure this names (Alan, 2026-08-04). It used to
// DISABLE the button whenever any leg was incomplete; since 2026-08-12 an
// incomplete leg is HELD while the ready ones go out ("dosent mean all
// information need to filled before start a task"), so these are now the notes
// beside the button naming what this press will not send - the button itself is
// live as long as one leg is ready.
export function dispatchReleaseBlockers(row = {}) {
  return dispatchReleaseLegs(row).flatMap((leg) => {
    if (leg.sent) return [];
    const missing = [!leg.from && 'From', !leg.to && 'To'].filter(Boolean);
    return missing.length ? [`${leg.label}: ${missing.join(' and ')} missing`] : [];
  });
}

/**
 * What one press of the button would do right now: how many legs it would send,
 * and whether any named leg is still waiting. Mirrors buildDispatchReleasePlan's
 * decision (route-complete, not already sent) so the button never promises what
 * the callable will refuse.
 */
export function dispatchReleaseReadiness(row = {}) {
  const legs = dispatchReleaseLegs(row);
  // Every unsent named leg counts, including one whose driver is on the paired
  // EMPTY.
  //
  // It was excluded on 2026-08-17, correctly at the time: buildDispatchReleasePlan
  // read only this row's three driver fields, so counting a leg it could not send
  // would have lit the button over work the press could not do. The callable takes
  // the driver AND the route from the paired empty since 2026-08-19, so the
  // exclusion inverted - it now DISABLES the button over work the press CAN do,
  // which is what Alan hit: the server-side fix shipped and "Release Remaining
  // Legs" stayed grey, so George never got the empty.
  const unsent = legs.filter((leg) => !leg.sent);
  return {
    legs,
    sendable: unsent.filter((leg) => leg.from && leg.to).length,
    unsentNamed: unsent.length,
  };
}

export function dispatchReleaseState(row = {}) {
  const releasedAt = asDate(row.dispatch_released_at);
  return {
    released: row.dispatch_released === true || Boolean(releasedAt),
    releasedAt,
    legs: dispatchReleaseLegs(row),
  };
}

/**
 * What a dispatcher needs in front of them to send this Container out, gathered
 * where the release button is (Alan, 2026-08-16). Two sets, because the two kinds
 * of Container are sent on two different legs.
 *
 * The identity block is the same for both - it is what the driver checks the box
 * against at the gate. What differs is the leg: a load is picked up, an empty is
 * taken back.
 *
 * These are all real Container fields, so they save through the same callable and
 * the same policy the table cells use. Nothing here can write something the server
 * would refuse, and a role that may not write one of them gets it read-only.
 */
const DISPATCH_IDENTITY_FIELDS = Object.freeze([
  'container_no', 'chassis_no', 'size_type', 'weight_lbs',
  'terminal_name', 'ssl', 'pin', 'customer_chassis',
]);

const DISPATCH_LEG_FIELDS = Object.freeze({
  laden: Object.freeze([
    'pickup_appointment_at', 'pickup_driver_name',
    // Pickup From has no field of its own - it is the terminal, or on a paired
    // release the yard the empty went back to - so it is shown resolved rather
    // than as an editable blank that writes nowhere.
    'delivery_warehouse_address',
  ]),
  empty: Object.freeze([
    'return_empty_lfd_at', 'return_driver_name',
    'empty_from_address', 'empty_to_address',
  ]),
});

// The transfer, when this Container has one (Alan, 2026-08-16). Added to whichever
// leg the Container is already on rather than replacing it: a load with a yard
// move has a pickup AND a transfer, and the operator sends both from here.
const DISPATCH_TRANSFER_FIELDS = Object.freeze([
  'transfer_from_address', 'transfer_to_address', 'transfer_driver_name', 'transfer_at',
]);

function rowHasTransfer(row) {
  return DISPATCH_TRANSFER_FIELDS.some((key) => cleanText(row?.[key]));
}

function dispatchDetailFields(row, columns, role) {
  const kind = rowIsReportedEmpty(row) ? 'empty' : 'laden';
  const byKey = new Map((Array.isArray(columns) ? columns : []).map((column) => [column.key, column]));
  // Whoever may WRITE the transfer sees its four fields whether or not they are
  // filled in yet (Alan, 2026-08-17: "dispatcher现在改不了transfer driver").
  //
  // They used to appear only when one of the four already carried a value - and
  // Transfer Driver is one of the four. So on a Container with no transfer yet the
  // block was absent, and the only way to add a transfer through this tab was to
  // have already added one somewhere else. Production says how often that bites:
  // of 724 Containers exactly ONE carries a transfer_from_address and none carry a
  // transfer driver, so the block was hidden on essentially every Container.
  //
  // The original reason still holds for everyone else - "four more fields on every
  // Container that will never have a yard move is four more things to read past" -
  // so a role that can only read them still sees them only when there is something
  // to read.
  const mayWriteTransfer = DISPATCH_TRANSFER_FIELDS.some((key) => {
    const column = byKey.get(key);
    return column ? columnEditableForRole(column, role) : false;
  });
  return [
    ...DISPATCH_IDENTITY_FIELDS,
    ...DISPATCH_LEG_FIELDS[kind],
    ...(rowHasTransfer(row) || mayWriteTransfer ? DISPATCH_TRANSFER_FIELDS : []),
    // Last, and on all three legs: a note written while deciding the dispatch,
    // before the driver is sent (Alan, 2026-08-16: 以上三个我说的全部在tab派单
    // 之前可以添加备注). The same hold_note the table and the Notes card show,
    // so it is one note per Container rather than a second one only this panel
    // can see.
    'hold_note',
  ]
    .map((key) => byKey.get(key))
    .filter(Boolean);
}

/**
 * The dispatch details, as editable controls beside the release button.
 *
 * Alan, 2026-08-16: the operator picks a Container, presses next, and everything
 * needed to send it out is in one block - identity to check the box against at the
 * gate, then the leg. Before this the fields were reachable, but in the other card
 * behind an Edit toggle, so deciding a dispatch meant reading in one place and
 * typing in another.
 *
 * Saved one field at a time through the same callable the table cells use, rather
 * than through the edit form beside it: that form is a batch with a Save button and
 * lives in a different card, and a second copy of the same fields inside it would
 * be two editors racing on one Container.
 */
function dispatchDetailsMarkup(row, role, columns, driverOptions, addressCatalog) {
  const fields = dispatchDetailFields(row, columns, role);
  if (!fields.length) return '';
  const legs = dispatchReleaseLegs(row);
  const pickup = legs.find((leg) => leg.key === 'pickup');
  const resolved = rowIsReportedEmpty(row) ? [] : [
    // Pickup From has no field of its own: it is the terminal, or on a paired
    // release the yard the empty was just returned to. Read-only because writing
    // it would write to whichever field happened to win, which is not a thing the
    // operator asked for.
    ['Pickup From', pickup?.from || '—'],
    ['Pickup To', pickup?.to || '—'],
  ];
  const control = (column) => {
    // Role, and the one row-level rule that reaches these fields: a load on the
    // customer's own chassis has no Chassis # for us to assign, and the callable
    // refuses the write.
    const editable = columnEditableForRole(column, role)
      && !(column.key === 'chassis_no' && rowUsesCustomerOwnChassis(row))
      && !(column.key === 'container_no' && !containerNumberEditable(row, role));
    return editable
      ? editorControlMarkup(column, row, driverOptions, false, addressCatalog)
          .replace('data-task-modal-field=', 'data-dispatch-field=')
      : `<p class="container-task-modal__dispatch-readonly">${escapeHtml(cleanText(row?.[column.key]) || '—')}</p>`;
  };
  return `<div class="container-task-modal__dispatch">
    ${fields.map((column) => `<label class="container-task-modal__dispatch-field">
      <span>${escapeHtml(column.label)}</span>
      ${control(column)}
    </label>`).join('')}
    ${resolved.map(([label, value]) => `<div class="container-task-modal__dispatch-field">
      <span>${escapeHtml(label)}</span>
      <p class="container-task-modal__dispatch-readonly">${escapeHtml(value)}</p>
    </div>`).join('')}
    <p class="container-task-modal__dispatch-error" data-dispatch-error role="alert"></p>
  </div>`;
}

function dispatchReleaseMarkup(row, role, {
  drivers = [], driverProfilesEnabled = false, columns = [], addressCatalog = null,
} = {}) {
  // Only the two roles the callable accepts see the control; everyone else sees the
  // state. Drawing it for a clerk would be a button that always fails.
  //
  // And never on an empty (Alan, 2026-08-16: "空柜子不能有派单的功能"). An empty is
  // not dispatched from its own record - its return is a LEG of the load that
  // paired it, released from that load's task, which is why every release in the
  // pairing flow names the laden's id. A button here would either do nothing or
  // dispatch the wrong Container.
  const token = roleToken(role);
  // An empty that a load has already claimed is released from THAT load - its
  // return is a leg of that trip, and a second button here would send the same
  // return to a second driver. An unclaimed empty still has to get back to the
  // terminal, and nothing else will send it, so it keeps its own release
  // (Alan, 2026-08-16, resolving the two halves of this).
  const mayRelease = (token === 'dispatcher' || token === 'admin')
    && !(rowIsReportedEmpty(row) && rowEmptyIsClaimed(row));
  // Money rides on the leg it was spent on, for the roles that may see it - the
  // same viewing right the field policy grants, applied here because these rows
  // are drawn outside it.
  const showsMoney = FINANCE_ROLES.has(roleToken(role));
  const ids = driverIdsByName(drivers);
  const { released, releasedAt, legs } = dispatchReleaseState(row);
  const blockers = dispatchReleaseBlockers(row);
  const legList = legs.length
    ? `<ul class="container-task-modal__release-legs">${legs.map((leg) => {
      const route = leg.from || leg.to
        ? `<p class="release-leg__route"><span>${escapeHtml(leg.from || '—')}</span><i aria-hidden="true">→</i><span>${escapeHtml(leg.to || '—')}</span></p>`
        : '';
      const shown = [
        ...leg.facts,
        ...(showsMoney && leg.cost !== null
          ? [{ label: 'Cost', value: `$${(leg.cost / 100).toFixed(2)}` }]
          : []),
      ];
      const facts = shown.length
        ? `<dl class="release-leg__facts">${shown.map((fact) => `<div><dt>${escapeHtml(fact.label)}</dt><dd>${escapeHtml(fact.value)}</dd></div>`).join('')}</dl>`
        : '';
      // No route and no facts means there is nothing behind the arrow, so the
      // arrow is not drawn - an expander that opens onto nothing is worse than
      // no expander.
      const body = route + facts;
      const avatar = driverAvatarMarkup(leg.name, {
        className: 'release-leg__avatar', ids, enabled: driverProfilesEnabled,
      });
      // "Waiting to release": named, not yet sent, no progress dates. Reads
      // differently from "Not started", which since 2026-08-12 can describe a
      // SENT leg the driver has not begun - a partial release leaves both on
      // one Container and the two must not look alike.
      // A leg drawn before its driver arrives - the paired Empty Return - says
      // which of the two it is waiting for, so "no driver yet" is never mistaken
      // for "ready and waiting to be sent".
      const progress = !leg.name ? 'Waiting for a driver'
        // No special case for a driver read off the paired empty any more: this
        // button sends that leg too, so it waits to be released like the rest.
        : !leg.sent && leg.progress === 'Not started' ? 'Waiting to release' : leg.progress;
      const head = `<strong>${escapeHtml(leg.label)}</strong>${avatar}<span>${escapeHtml(leg.name || 'Unassigned')}</span><small>${escapeHtml(progress)}</small>`;
      return `<li data-release-state="${escapeHtml(leg.state)}"${leg.sent ? ' data-release-sent="true"' : ''}>${body
        ? `<details class="release-leg"><summary class="release-leg__head">${head}</summary><div class="release-leg__body">${body}</div></details>`
        : `<div class="release-leg__head release-leg__head--flat">${head}</div>`}</li>`;
    }).join('')}</ul>`
    : '<p class="container-task-modal__empty">No driver has been named on any leg yet.</p>';
  const details = mayRelease ? dispatchDetailsMarkup(row, role, columns, drivers, addressCatalog) : '';
  const { sendable, unsentNamed } = dispatchReleaseReadiness(row);
  // The fee gate. Every leg through a terminal gate waits on CTF/TMF; a transfer
  // is a yard move on our side of it and does not (Alan, 2026-08-16). So the
  // button stays live when the only leg still to send is the transfer, and is
  // otherwise held with the reason beside it.
  const feeBlocker = pairingReleaseBlocker(row);
  const onlyTransferWaits = legs.filter((leg) => !leg.sent).every((leg) => leg.key === 'transfer');
  const feeHeld = TERMINAL_FEES_HOLD_RELEASE && Boolean(feeBlocker) && !onlyTransferWaits;
  // The notice is shown whether or not it holds: an unsettled fee is worth saying
  // even when it is not worth refusing over.
  const feeNote = Boolean(feeBlocker) && !onlyTransferWaits;
  // Taking it back. Until 2026-08-16 the only way to reach a cancellation was to
  // go and start a SECOND assignment on the same Container and be refused with
  // ASSIGNMENT_ACTIVE_CONFLICT - the cancel form lived on that refusal. So the
  // operator had to provoke an error to undo a dispatch (Alan: "release driver
  // 之后打开那个task 旁边可以取消派单"). It belongs where the release is.
  //
  // Same two roles as the release, because cancelDispatchAssignment accepts the
  // same two. The task id and its updated_at are NOT on this row - the row carries
  // a task summary without it - so the handler in app.js reads the task when the
  // button is pressed, exactly as the old form did.
  const cancelButton = mayRelease && released
    ? `<button type="button" class="container-task-modal__release-cancel" data-task-cancel-dispatch><i class="ph ph-arrow-u-up-left" aria-hidden="true"></i>取消派单 Cancel Dispatch</button>`
    : '';
  // Fully out: released and nothing named is waiting. A press of RELEASE here
  // would only be refused, so no release button is drawn - but it is exactly the
  // state where taking it back is the only thing left to do.
  if (released && unsentNamed === 0) {
    return `<div class="container-task-modal__release is-released">
      <p class="container-task-modal__release-status"><i class="ph ph-check-circle" aria-hidden="true"></i><span>Released to drivers${releasedAt ? ` · ${escapeHtml(formattedDate(releasedAt))}` : ''}</span></p>
      ${details}
      ${legList}
      ${cancelButton}
      <p class="container-task-modal__release-error" data-task-release-error role="alert"></p>
    </div>`;
  }
  // Not released, or partially released with named legs still waiting (Alan,
  // 2026-08-12: start the task with what is ready; the rest goes out when its
  // information is filled in). The button is live as long as ONE leg is ready -
  // the blockers beside it name what this press will hold, they no longer
  // disable it.
  const label = released ? 'Release Remaining Legs' : 'Release to Drivers';
  return `<div class="container-task-modal__release">
    ${released ? `<p class="container-task-modal__release-status"><i class="ph ph-check-circle" aria-hidden="true"></i><span>Partially released${releasedAt ? ` · ${escapeHtml(formattedDate(releasedAt))}` : ''}</span></p>` : ''}
    ${details}
    ${legList}
    ${mayRelease
    ? `<button type="button" class="container-task-modal__release-button" data-task-release${sendable && !feeHeld ? '' : ' disabled'}><i class="ph ph-paper-plane-tilt" aria-hidden="true"></i>${escapeHtml(label)}</button>
      ${cancelButton}
      ${feeNote ? `<p class="container-task-modal__release-fee-hold" role="status"><i class="ph ph-lock" aria-hidden="true"></i>CTF/TMF 未交 — not settled on this Container.${feeHeld ? ' It cannot be released; a transfer may still go.' : ''}</p>` : ''}
      ${blockers.length ? `<ul class="container-task-modal__release-blockers" role="status">${blockers.map((reason) => `<li>${escapeHtml(reason)} — will be held</li>`).join('')}</ul>` : ''}`
    : '<p class="container-task-modal__release-hint">Not yet fully released by Dispatch.</p>'}
    <p class="container-task-modal__release-error" data-task-release-error role="alert"></p>
  </div>`;
}

// The legs are the card (Alan, 2026-08-05). A per-driver list underneath repeated
// each leg's route, facts and progress a second time, so the panel said everything
// twice and the operator read the same run in two shapes to be sure they matched.
// One list, one place: each leg names its driver, carries their initials for the
// profile, and opens onto the detail for that leg.
function driverInformationMarkup(row, drivers, driverProfilesEnabled, role, columns, addressCatalog) {
  return dispatchReleaseMarkup(row, role, { drivers, driverProfilesEnabled, columns, addressCatalog });
}

function editorInputValue(column, row) {
  const value = row?.[column.key];
  if (column.key === 'customer_paid') return value === true ? 'customer_paid' : '';
  if (column.editor === 'date') {
    return dashboardEditorInputSpec(column, asDate(value)).value;
  }
  if (column.editor === 'currency') {
    return Number.isSafeInteger(value) ? (value / 100).toFixed(2) : '';
  }
  return cleanText(value);
}

function selectOptionsMarkup(column, currentValue) {
  const options = [...new Set((Array.isArray(column.options) ? column.options : [])
    .map((value) => cleanText(value)).filter(Boolean))];
  if (currentValue && !options.includes(currentValue)) options.unshift(currentValue);
  const placeholder = column.key === 'customer_paid'
    ? '<option value="">Not paid</option>'
    : '<option value="">Select</option>';
  return `${placeholder}${options.map((option) => (
    `<option value="${escapeHtml(option)}"${currentValue === option ? ' selected' : ''}>${escapeHtml(humanize(option))}</option>`
  )).join('')}`;
}

function addressEditorMarkup(column, row, addressCatalog, disabled, shared) {
  const currentValue = cleanText(row?.[column.key]);
  const options = (Array.isArray(addressCatalog?.options) ? addressCatalog.options : [])
    .filter((option) => option && cleanText(option.address));
  if (!options.length) {
    return `<textarea name="${escapeHtml(column.key)}" rows="2" ${shared}>${escapeHtml(currentValue)}</textarea>`;
  }
  // Empty To is a closed list of terminals, so it is the dropdown alone - no
  // Custom entry and no textarea to type past it (Alan, 2026-08-05). The hidden
  // input still carries the value so the form reads it the same way.
  const closedList = column.key === 'empty_to_address';
  const custom = !closedList && customAddressChip(column, row, addressCatalog) !== null;
  const optionsMarkup = options.map((option) => {
    const address = cleanText(option.address);
    return `<option value="${escapeHtml(address)}"${!custom && address === currentValue ? ' selected' : ''}>${escapeHtml(cleanText(option.label) || address)}</option>`;
  }).join('');
  return `<div class="container-task-modal__address-editor">
    <select name="${escapeHtml(column.key)}__catalog" data-task-modal-address-select="${escapeHtml(column.key)}"${disabled ? ' disabled' : ''}>
      <option value="">Select saved address</option>
      ${optionsMarkup}
      ${closedList ? '' : `<option value="${escapeHtml(CUSTOM_ADDRESS_VALUE)}"${custom ? ' selected' : ''}>Custom address</option>`}
    </select>
    ${closedList
    ? `<input type="hidden" name="${escapeHtml(column.key)}" ${shared} value="${escapeHtml(currentValue)}">`
    : `<textarea name="${escapeHtml(column.key)}" rows="2" ${shared}>${escapeHtml(currentValue)}</textarea>`}
  </div>`;
}

function editorControlMarkup(column, row, driverOptions, disabled, addressCatalog = null) {
  const currentValue = editorInputValue(column, row);
  const shared = `data-task-modal-field="${escapeHtml(column.key)}" data-original="${escapeHtml(currentValue)}"${disabled ? ' disabled' : ''}`;
  if (column.editor === 'actual_empty_return') {
    const date = asDate(row?.[column.key]);
    const dateValue = date ? dashboardEditorInputSpec({
      ...column,
      editor: 'date',
      format: 'date-only',
    }, date).value : '';
    const timeValue = row?.actual_empty_returned_precision === 'date_time' && date
      ? dashboardEditorInputSpec({
        ...column,
        editor: 'date',
        format: 'date-time',
      }, date).value.slice(11) : '';
    return `<div class="container-task-modal__actual-return" data-task-modal-field="${escapeHtml(column.key)}" data-original-date="${escapeHtml(dateValue)}" data-original-time="${escapeHtml(timeValue)}">
      <input name="${escapeHtml(column.key)}__date" type="date" value="${escapeHtml(dateValue)}"${disabled ? ' disabled' : ''}>
      <input name="${escapeHtml(column.key)}__time" type="time" value="${escapeHtml(timeValue)}"${disabled ? ' disabled' : ''}>
    </div>`;
  }
  if (column.editor === 'driver') {
    // A name, not an account (Alan, 2026-09-28: "driver的下拉选项需要有自定义选项不能
    // 只是单个的下拉而是加一个自定义"): pickup/return/transfer driver here is the same
    // plain text field the Dashboard cell already lets be typed
    // (dashboard-driver-dropdown.mjs's typedDriverName) - a subcontracted or
    // one-off driver has no business record to pick, so a closed list would have
    // no entry for them. An input with the known names attached, exactly like the
    // allowCustom select just below, rather than a second control to reveal.
    const normalized = normalizeDashboardDrivers(driverOptions);
    const choices = normalized.map(({ fullName }) => fullName);
    const listId = `container-task-modal-drivers-${escapeHtml(column.key)}`;
    return `<input name="${escapeHtml(column.key)}" type="text" list="${listId}" autocomplete="off" placeholder="Unassigned" value="${escapeHtml(currentValue)}" ${shared}><datalist id="${listId}">${
      choices.map((name) => `<option value="${escapeHtml(name)}"></option>`).join('')
    }</datalist>`;
  }
  if (column.editor === 'select' && column.allowCustom) {
    // An open menu, so the control is an input with the list attached rather than
    // a <select>: the operator picks a listed value or types one that is not
    // listed, in one control. A <select> here would need a Custom option plus a
    // second box to reveal, and that second box would be a second thing for
    // editRequestsFromForm to collect. This one is collected exactly like every
    // other text field.
    //
    // Options are drawn verbatim - humanize() would render the carrier COSCO as
    // "Cosco" and the terminal PCT as "Pct".
    const listId = `container-task-modal-options-${escapeHtml(column.key)}`;
    // Same assembled list the table's menu offers - seeded values plus everything
    // the fleet has taught it. Two surfaces, one list, or a value typed here is
    // missing there.
    const options = [...new Set(fieldOptions(column).map((value) => cleanText(value)).filter(Boolean))];
    return `<input name="${escapeHtml(column.key)}" type="text" list="${listId}" autocomplete="off" value="${escapeHtml(currentValue)}" ${shared}><datalist id="${listId}">${
      options.map((option) => `<option value="${escapeHtml(option)}"></option>`).join('')
    }</datalist>`;
  }
  if (column.editor === 'select') {
    return `<select name="${escapeHtml(column.key)}" ${shared}>${selectOptionsMarkup(column, currentValue)}</select>`;
  }
  if (column.editor === 'date') {
    const spec = dashboardEditorInputSpec(column, asDate(row?.[column.key]));
    const typed = spec.type === 'text' ? ` placeholder="${escapeHtml(spec.placeholder ?? '')}" autocomplete="off"` : '';
    return `<input name="${escapeHtml(column.key)}" type="${spec.type}" value="${escapeHtml(spec.value)}"${typed} ${shared}>`;
  }
  if (column.editor === 'address') {
    return addressEditorMarkup(column, row, addressCatalog, disabled, shared);
  }
  if (column.editor === 'note' && !APPEND_ONLY_NOTE_FIELDS.has(column.key)) {
    // An ordinary note is an ordinary text box here too (Alan, 2026-09-25): it
    // opens holding the note, and data-original is the note, so an untouched box
    // sends nothing and an emptied one clears it.
    return `<textarea name="${escapeHtml(column.key)}" rows="4" ${shared}>${escapeHtml(currentValue)}</textarea>`;
  }
  if (column.editor === 'note') {
    // Empty box, existing note above it, same rule as the Dashboard cell editor
    // (Alan, 2026-08-11): what is typed is added to the note, never replaces it.
    // data-original is deliberately blank rather than the note - editRequestsFromForm
    // compares against it, and comparing an addition to the whole note would send
    // the box on every save and send nothing when someone retypes what is there.
    const noteShared = `data-task-modal-field="${escapeHtml(column.key)}" data-original=""${disabled ? ' disabled' : ''}`;
    return `<div class="container-task-modal__note-editor">${
      currentValue ? `<div class="container-task-modal__note-kept" role="note" aria-label="${escapeHtml(`${column.label} so far`)}">${escapeHtml(currentValue)}</div>` : ''
    }<textarea name="${escapeHtml(column.key)}" rows="3" placeholder="${currentValue ? 'Add to this note' : 'Write the note'}" ${noteShared}></textarea></div>`;
  }
  if (column.key === 'size_type') {
    return `<input name="${escapeHtml(column.key)}" type="text" list="taskModalContainerTypes" value="${escapeHtml(currentValue)}" ${shared}>`;
  }
  const type = column.editor === 'currency' || column.editor === 'number' ? 'number' : 'text';
  const numeric = type === 'number'
    ? ` min="0" step="${column.editor === 'currency' ? '0.01' : 'any'}"` : '';
  return `<input name="${escapeHtml(column.key)}" type="${type}" value="${escapeHtml(currentValue)}"${numeric} ${shared}>`;
}

function readBandMarkup(entries, variant) {
  if (!entries.length) return '';
  return `<dl class="container-task-modal__band container-task-modal__band--${escapeHtml(variant)}">${entries.map((entry) => `
    <div class="container-task-modal__field${variant === 'editable' ? ' is-editable' : ''}" data-task-modal-field-key="${escapeHtml(entry.key)}">
      <dt>${escapeHtml(entry.label)}</dt>
      <dd>${valueMarkup(entry.value)}${customChipMarkup(entry.chip)}</dd>
    </div>`).join('')}
  </dl>`;
}

function editBandMarkup(entries, { row, driverOptions, editSaving, addressCatalog }) {
  if (!entries.length) return '';
  return `<div class="container-task-modal__edit-grid">${entries.map((entry) => `
    <label class="container-task-modal__field is-editable" data-task-modal-field-key="${escapeHtml(entry.key)}">
      <span>${escapeHtml(entry.label)}${entry.chip ? customChipMarkup('Custom') : ''}</span>
      ${editorControlMarkup(entry.column, row, driverOptions, editSaving, addressCatalog)}
    </label>`).join('')}
  </div>`;
}

// Dispatch/Admin's no-evidence-yet path onto canonical completion (Alan, confirmed
// after being told the exact consequences), drawn beside the Empty Return fields
// it acts on rather than the Driver-evidence review flow it does not replace.
// isEmptyReturnGroup callers gate this to editMode === false: the button starts a
// callable, it does not belong inside the field-editing form.
function manuallyCompleteEmptyReturnMarkup({ canComplete, pending }) {
  if (pending) {
    return `<p class="container-task-modal__manual-empty-return-note" role="status">
      <i class="ph ph-info" aria-hidden="true"></i>
      <span>A Driver has already submitted Empty Return evidence — use the Empty Return review to confirm or return it. 已有司机提交还空证据，请使用还空审核确认或退回。</span>
    </p>`;
  }
  if (!canComplete) return '';
  return `<div class="container-task-modal__manual-empty-return">
    <button type="button" class="container-task-modal__manual-empty-return-button" data-task-modal-manual-empty-return>
      <i class="ph ph-check-circle" aria-hidden="true"></i><span>Mark Empty Returned 标记还空完成</span>
    </button>
    <p class="container-task-modal__manual-empty-return-error" data-task-modal-manual-empty-return-error role="alert"></p>
  </div>`;
}

// One layout serves both modes. Edit mode swaps the editable band's values for controls in place —
// it never opens a panel or a dialog, so the fields stay exactly where the operator just read them.
function lifecycleGroupsMarkup({
  row, role, columnsByKey, formatValue, addressCatalog, editMode,
  editableColumns, driverOptions, editSaving, pendingEmptyReturnReview = false,
}) {
  const groups = containerTaskModalLifecycleGroups({
    row, columnsByKey, role, formatValue, addressCatalog, editMode, editableColumns,
  });
  if (!groups.length) {
    return '<p class="container-task-modal__empty">No information is available yet.</p>';
  }
  const normalizedRole = roleToken(role);
  const containerAlreadyComplete = row?.status === 'empty_returned' && row?.is_empty_returned === true;
  const canManuallyComplete = !editMode && !containerAlreadyComplete
    && ['dispatcher', 'admin'].includes(normalizedRole);
  return `<div class="container-task-modal__lifecycle">${groups.map((group) => {
    const editable = editMode
      ? editBandMarkup(group.editable, {
        row, driverOptions, editSaving, addressCatalog,
      })
      : readBandMarkup(group.editable, 'editable');
    const readonly = readBandMarkup(group.readonly, 'readonly');
    const manualEmptyReturn = !editMode && group.key === 'empty_return'
      ? manuallyCompleteEmptyReturnMarkup({
        canComplete: canManuallyComplete,
        pending: pendingEmptyReturnReview,
      })
      : '';
    return `<section class="container-task-modal__group" data-task-modal-field-group="${escapeHtml(group.key)}" aria-label="${escapeHtml(group.label)}">
      ${groupHeading(group.label)}
      ${readonly}
      ${readonly && editable ? '<hr class="container-task-modal__band-divider">' : ''}
      ${editable}
      ${manualEmptyReturn}
    </section>`;
  }).join('')}</div>`;
}

function overviewMarkup({
  row, role, columnsByKey, formatValue, related, driverProfilesEnabled, addressCatalog,
  editMode, editableColumns, driverOptions, editLoading, editSaving, editError,
  pendingEmptyReturnReview = false,
}) {
  const timeline = taskTimelineItems(row);
  const drivers = Array.isArray(related?.drivers) ? related.drivers : [];
  const driverIds = driverIdsByName(drivers);
  // Finance reads everything Admin reads (Alan, 2026-08-04) - it is a viewing
  // right, not an editing one, so these gates widen and the edit gates do not.
  const customerCard = FINANCE_ROLES.has(roleToken(role)) && related?.customer
    ? card('Customer Information', relatedDefinitionList(related.customer))
    : '';
  const groups = lifecycleGroupsMarkup({
    row,
    role,
    columnsByKey,
    formatValue,
    addressCatalog,
    editMode,
    editableColumns,
    driverOptions,
    editSaving,
    pendingEmptyReturnReview,
  });
  const body = editMode
    ? `${editLoading ? '<p class="container-task-modal__edit-status" role="status">Loading current editor options…</p>' : ''}
    <form data-task-modal-edit-form>
      <datalist id="taskModalContainerTypes"><option value="40"></option><option value="40HQ"></option><option value="45"></option></datalist>
      <p class="container-task-modal__edit-hint">Only fields assigned to your role are available here.</p>
      ${groups}
      <p class="container-task-modal__edit-error" data-task-modal-edit-error role="alert">${escapeHtml(editError || '')}</p>
      <div class="container-task-modal__edit-actions">
        <button type="button" data-task-modal-edit-cancel${editSaving ? ' disabled' : ''}>Cancel</button>
        <button type="submit"${editSaving || editLoading ? ' disabled' : ''}><i class="ph ph-check" aria-hidden="true"></i>${editSaving ? 'Saving…' : 'Save Changes'}</button>
      </div>
    </form>`
    : groups;
  return `<div class="container-task-modal__overview">
    <div class="container-task-modal__overview-main">
      ${card('Container Information', body, 'container-task-modal__card--lifecycle')}
      ${customerCard}
    </div>
    <aside class="container-task-modal__overview-side">
      ${card('Timeline', timeline.length ? `<ol class="container-task-modal__timeline">
        ${timeline.map((item, index) => `<li class="${index === timeline.length - 1 ? 'is-current' : 'is-complete'}">
          ${item.who
    ? driverAvatarMarkup(item.who, {
      className: 'container-task-modal__timeline-avatar',
      ids: driverIds,
      enabled: driverProfilesEnabled,
    })
    : `<i class="ph ${escapeHtml(item.icon)}" aria-hidden="true"></i>`}
          <div><strong>${escapeHtml(item.label)}</strong><span>${escapeHtml(humanize(item.field))}</span><time>${escapeHtml(formattedDate(item.date))}</time></div>
        </li>`).join('')}
      </ol>` : '<p class="container-task-modal__empty">No Task milestones have been recorded yet.</p>')}
      ${roleToken(role) === 'customer' && !drivers.length && !dispatchReleaseState(row).legs.length
    ? ''
    : card('Driver Information', driverInformationMarkup(row, drivers, driverProfilesEnabled, role, editableColumns, addressCatalog))}
    </aside>
  </div>`;
}

function podEvidence(items) {
  return (Array.isArray(items) ? items : []).filter((item) => (
    item && typeof item === 'object' && item.kind === 'pod'
  ));
}

function podMarkup({
  row, evidence, podEditable, podEditing, evidenceLoading, evidenceError,
}) {
  if (podEditing) {
    return `<section class="container-task-modal__editor">
      <header><div><h3>Manage POD</h3></div>
        <button type="button" class="container-task-modal__text-button" data-task-modal-cancel-pod>Back to POD Summary</button>
      </header>
      <div data-task-modal-pod-editor-host>
        <p class="container-task-modal__empty" role="status">Loading POD editor…</p>
      </div>
    </section>`;
  }
  let body = '';
  const items = podEvidence(evidence);
  if (evidenceLoading) {
    body = '<p class="container-task-modal__empty" role="status">Loading POD files…</p>';
  } else if (evidenceError) {
    body = '<p class="container-task-modal__empty is-error" role="alert">POD files could not be loaded.</p>';
  } else if (!items.length) {
    body = '<p class="container-task-modal__empty">No POD files are available yet.</p>';
  } else {
    body = `<div class="container-task-modal__pod-list">${items.map((item, index) => {
      const objectUrl = cleanText(item.objectUrl);
      const status = humanize(item.status || item.dispatcher_status || item.operational_status);
      return `<article class="container-task-modal__pod-item">
        <div class="container-task-modal__pod-preview">
          ${objectUrl
    ? `<img src="${escapeHtml(objectUrl)}" alt="POD file ${index + 1} for ${escapeHtml(row.container_no || 'Container')}">`
    : '<div><i class="ph ph-file-image" aria-hidden="true"></i><span>Preview unavailable</span></div>'}
        </div>
        <div class="container-task-modal__pod-meta">
          <span class="container-task-modal__file-number">File ${index + 1}</span>
          <dl>
            <div><dt>Status</dt><dd class="is-success">${escapeHtml(status)}</dd></div>
            <div><dt>Received</dt><dd>${escapeHtml(formattedDate(item.created_at))}</dd></div>
            <div class="is-wide"><dt>Note</dt><dd>${escapeHtml(cleanText(item.note) || 'No note')}</dd></div>
          </dl>
          ${objectUrl ? `<div class="container-task-modal__file-actions">
            <a href="${escapeHtml(objectUrl)}" target="_blank" rel="noopener"><i class="ph ph-eye" aria-hidden="true"></i>View</a>
            <a href="${escapeHtml(objectUrl)}" download><i class="ph ph-download-simple" aria-hidden="true"></i>Download</a>
          </div>` : ''}
        </div>
      </article>`;
    }).join('')}</div>`;
  }
  return card('POD Summary', `
    <div class="container-task-modal__card-heading">
      <span>${items.length} file${items.length === 1 ? '' : 's'}</span>
      ${podEditable ? '<button type="button" data-task-modal-manage-pod><i class="ph ph-upload-simple" aria-hidden="true"></i>Manage POD</button>' : ''}
    </div>
    ${body}`, 'container-task-modal__card--pod');
}

function notesMarkup({
  row, role, columnsByKey, formatValue, related,
}) {
  const notes = visibleEntries(FIELD_GROUPS.notes, row, columnsByKey, formatValue);
  if (FINANCE_ROLES.has(roleToken(role))) {
    if (related?.customer?.delivery_notes) {
      notes.push({ label: 'Customer Delivery Notes', value: cleanText(related.customer.delivery_notes) });
    }
    for (const driver of Array.isArray(related?.drivers) ? related.drivers : []) {
      if (driver.note) notes.push({
        label: `${driver.name || 'Driver'} Note`,
        value: cleanText(driver.note),
      });
    }
  }
  return card('Notes', notes.length
    ? `<div class="container-task-modal__notes">${notes.map((note) => `
      <article><span>${escapeHtml(note.label)}</span><p>${escapeHtml(note.value)}</p></article>`).join('')}</div>`
    : '<p class="container-task-modal__empty">No notes have been recorded for this Task.</p>');
}

function expensesMarkup({
  row, columnsByKey, formatValue,
}) {
  const groups = containerTaskModalExpenseGroups(row, columnsByKey, formatValue);
  return card('Expenses', groups.length
    ? `<div class="container-task-modal__expense-groups">${groups.map((group) => (group.isEmpty
      ? `<details class="container-task-modal__expense-group is-empty" data-task-modal-expense-group="${escapeHtml(group.key)}">
        <summary>${escapeHtml(group.label)}<span>${group.entries.length} empty</span></summary>
        ${definitionList(group.entries, 'container-task-modal__definition-list--expenses')}
      </details>`
      : `<section class="container-task-modal__expense-group" data-task-modal-expense-group="${escapeHtml(group.key)}" aria-label="${escapeHtml(group.label)}">
        ${groupHeading(group.label)}
        ${definitionList(group.entries, 'container-task-modal__definition-list--expenses')}
      </section>`)).join('')}</div>`
    : '<p class="container-task-modal__empty">No authorized expense fields are available.</p>');
}

/**
 * The DO's own place on the Container's page, beside its POD count (Alan,
 * 2026-09-27: "我需要有专门的一个位置显示那个链接").
 *
 * The uploaded file was stored and then never reachable: the page printed
 * "Delivery order (uploaded)" as text, and nothing could read the file back. Now
 * it is one press - the host opens it - and a Container with no DO says so rather
 * than leaving a gap. Nothing is claimed while the details are still loading,
 * because the DO arrives with them.
 */
export function deliveryOrderMarkup(row, { openable = false, loading = false } = {}) {
  const stored = row?.do_ref;
  const objectPath = deliveryOrderObjectPath(stored);
  const label = '<i class="ph ph-file-text" aria-hidden="true"></i><span>DO</span>';
  if (objectPath && openable) {
    return `<button type="button" class="container-task-modal__do" data-task-modal-do="${escapeHtml(stored)}" title="打开 DO · Open the delivery order" aria-label="打开 DO · Open the delivery order">${label}</button>`;
  }
  const link = deliveryOrderLink(stored);
  if (link) {
    return `<a class="container-task-modal__do" href="${escapeHtml(link)}" target="_blank" rel="noopener noreferrer" title="打开 DO · Open the delivery order">${label}</a>`;
  }
  if (loading) return '';
  return `<span class="container-task-modal__do is-missing" title="没有上传 DO · No delivery order uploaded">${label}<span aria-hidden="true">—</span></span>`;
}

export function containerTaskModalMarkup({
  row: sourceRow = {},
  role = '',
  columns = [],
  formatValue = (_key, row) => row[_key],
  detail = null,
  related = null,
  evidence = [],
  podEditable = false,
  podEditing = false,
  detailsLoading = false,
  detailsError = false,
  evidenceLoading = false,
  evidenceError = false,
  canEditFields = false,
  editMode = false,
  editLoading = false,
  editSaving = false,
  editError = '',
  driverOptions = [],
  driverProfilesEnabled = false,
  addressCatalog = null,
  deliveryOrderOpenable = false,
  // A dispatch_empty_return_review Action is already open for this Container -
  // a Driver submitted evidence and it is awaiting Dispatch's decision through
  // the existing review flow, so the manual-completion button is not offered.
  // Handed in from app.js off the row it already has (current_action_type),
  // the same "computed by the caller, not queried by the modal" pattern
  // podEditable already uses.
  pendingEmptyReturnReview = false,
} = {}) {
  const normalizedRole = roleToken(role);
  const sections = containerTaskModalSections(normalizedRole);
  const row = mergedRoleSafeRow(sourceRow, detail, normalizedRole);
  const columnsByKey = columnMap(columns);
  const status = containerStatusPresentation(row.ocean_status || row.status);
  const evidenceCount = podEvidence(evidence).length;
  const overview = overviewMarkup({
    row,
    role: normalizedRole,
    columnsByKey,
    formatValue,
    related,
    driverProfilesEnabled,
    addressCatalog,
    editMode,
    editableColumns: editMode ? containerTaskModalEditableColumns(columns, normalizedRole) : [],
    driverOptions,
    editLoading,
    editSaving,
    editError,
    pendingEmptyReturnReview,
  });
  const pod = podMarkup({
    row,
    evidence,
    podEditable,
    podEditing,
    evidenceLoading,
    evidenceError,
  });
  const notes = notesMarkup({
    row,
    role: normalizedRole,
    columnsByKey,
    formatValue,
    related,
  });
  const expenses = FINANCE_ROLES.has(normalizedRole)
    ? expensesMarkup({ row, columnsByKey, formatValue })
    : '';

  return `<section class="container-task-modal__surface" data-container-task-modal data-edit-mode="${editMode ? 'on' : 'off'}">
    <header class="container-task-modal__header">
      <div>
        <span class="container-task-modal__eyebrow">Container</span>
        <div class="container-task-modal__identity">
          <h2 id="containerTaskModalTitle">${escapeHtml(row.container_no || 'Container')}</h2>
          ${canEditFields ? `<button type="button" class="container-task-modal__edit-toggle" data-task-modal-edit-toggle aria-label="Edit authorized Container information" aria-expanded="${editMode}"><i class="ph ph-pencil-simple" aria-hidden="true"></i></button>` : ''}
          <span class="container-task-modal__status container-task-modal__status--${escapeHtml(status.tone)}">${escapeHtml(status.label)}</span>
          <span class="container-task-modal__pod-count"><i class="ph ph-folder-simple" aria-hidden="true"></i>${evidenceCount || Number(row.pod_count || 0)}</span>
          ${deliveryOrderMarkup(row, { openable: deliveryOrderOpenable, loading: detailsLoading })}
        </div>
      </div>
    </header>
    <div class="container-task-modal__body">
      ${detailsLoading
    ? '<div class="container-task-modal__loading-line" role="status"><span></span>Loading current Task details…</div>'
    : ''}
      ${detailsError
    ? '<p class="container-task-modal__inline-warning">Some related details could not be loaded. The role-safe Dashboard record is still shown.</p>'
    : ''}
      <div class="container-task-modal__all-sections" aria-label="Complete Task information">
        <section data-task-modal-section="overview" aria-label="Overview">${overview}</section>
        <section data-task-modal-section="pod" aria-label="POD">${pod}</section>
        <section data-task-modal-section="notes" aria-label="Notes">${notes}</section>
        ${sections.some(({ key }) => key === 'expenses')
    ? `<section data-task-modal-section="expenses" aria-label="Expenses">${expenses}</section>`
    : ''}
      </div>
    </div>
  </section>`;
}

function cleanupCallback(value) {
  if (typeof value === 'function') return value;
  if (value && typeof value.destroy === 'function') return () => value.destroy();
  return () => {};
}

function editRequestsFromForm(form, columns) {
  return columns.flatMap((column) => {
    if (column.editor === 'actual_empty_return') {
      const wrapper = form.querySelector(`[data-task-modal-field="${column.key}"]`);
      const date = cleanText(form.elements.namedItem(`${column.key}__date`)?.value);
      const time = cleanText(form.elements.namedItem(`${column.key}__time`)?.value);
      if (date === cleanText(wrapper?.dataset.originalDate)
        && time === cleanText(wrapper?.dataset.originalTime)) return [];
      return [{
        field: column.key,
        value: dashboardEditorValue(column, { date, time }),
      }];
    }
    const control = form.elements.namedItem(column.key);
    if (!control || typeof control.value !== 'string') return [];
    const rawValue = control.value;
    if (rawValue === cleanText(control.dataset.original)) return [];
    return [{
      field: column.key,
      value: dashboardEditorValue(column, rawValue),
    }];
  });
}

export function createContainerTaskModal({
  documentRef = document,
  role = '',
  columns = [],
  formatValue = (_key, row) => row[_key],
  loadDetail,
  loadEvidence,
  mountPodEditor = null,
  onSaveField = null,
  loadDriverOptions = null,
  loadDriverProfile = null,
  renderDriverProfile = null,
  onDriverUpdate = null,
  onReleaseDispatch = null,
  // Taking a release back. Handed in for the same reason the release is: the task
  // id and its updated_at are not on this row, and the reason picker and the retry
  // controller already live in app.js beside the callable.
  onCancelDispatch = null,
  // Dispatch/Admin's no-evidence-yet path onto canonical Empty Return completion.
  // Same signature shape as onReleaseDispatch/onCancelDispatch: containerId in,
  // the callable's response data out, app.js owns the callable and the fleet
  // refresh that follows it.
  onManuallyCompleteEmptyReturn = null,
  addressCatalog = null,
  // The empties a load has already claimed, from the dashboard summary.
  claimedEmptyContainerNos = null,
  // (containerNo) => row, over the loaded fleet, for the paired empty's route.
  findEmptyByNumber = null,
  // (storedDoRef) => opens the uploaded delivery order; see deliveryOrderMarkup.
  openDeliveryOrder = null,
} = {}) {
  findPairedEmpty = typeof findEmptyByNumber === 'function' ? findEmptyByNumber : null;
  claimedEmptyNumbers = Array.isArray(claimedEmptyContainerNos)
    ? new Set(claimedEmptyContainerNos.map((value) => String(value ?? '').trim().toUpperCase()).filter(Boolean))
    : null;
  if (!documentRef?.body || typeof documentRef.createElement !== 'function'
    || typeof loadDetail !== 'function' || typeof loadEvidence !== 'function'
    || (onSaveField !== null && typeof onSaveField !== 'function')
    || (loadDriverOptions !== null && typeof loadDriverOptions !== 'function')
    || (loadDriverProfile !== null && typeof loadDriverProfile !== 'function')
    || (renderDriverProfile !== null && typeof renderDriverProfile !== 'function')
    || (onDriverUpdate !== null && typeof onDriverUpdate !== 'function')
    || (onCancelDispatch !== null && typeof onCancelDispatch !== 'function')
    || (onReleaseDispatch !== null && typeof onReleaseDispatch !== 'function')
    || (onManuallyCompleteEmptyReturn !== null && typeof onManuallyCompleteEmptyReturn !== 'function')) {
    throw new TypeError('Container Task modal dependencies are invalid.');
  }

  let dialog = null;
  let trigger = null;
  let generation = 0;
  let assetCleanup = () => {};
  let editorCleanup = () => {};
  let state = null;
  let profileLayer = null;
  let profileGeneration = 0;

  function releaseEditor() {
    editorCleanup();
    editorCleanup = () => {};
  }

  function releaseAssets() {
    assetCleanup();
    assetCleanup = () => {};
  }

  function closeDriverProfile() {
    profileGeneration += 1;
    profileLayer?.remove();
    profileLayer = null;
  }

  function close({ restoreFocus = true } = {}) {
    generation += 1;
    const focusTarget = trigger;
    trigger = null;
    releaseEditor();
    releaseAssets();
    closeDriverProfile();
    dialog?.remove();
    dialog = null;
    state = null;
    if (restoreFocus) focusTarget?.focus?.({ preventScroll: true });
  }

  async function refreshDetails(token = generation) {
    if (!state) return false;
    state.detailsLoading = true;
    state.detailsError = false;
    render();
    try {
      const result = await loadDetail(state.row.id);
      if (!state || token !== generation) return false;
      state.detail = result?.detail || result?.task || null;
      state.related = result?.related || null;
      state.detailsLoading = false;
      render();
      finishInitialSectionPosition();
      return true;
    } catch {
      if (!state || token !== generation) return false;
      state.detailsLoading = false;
      state.detailsError = true;
      render();
      finishInitialSectionPosition();
      return false;
    }
  }

  async function refreshEvidence(token = generation) {
    if (!state) return;
    state.evidenceLoading = true;
    state.evidenceError = false;
    render();
    try {
      const assets = await loadEvidence(state.row.id, {
        containerNo: state.row.container_no,
        podCount: state.row.pod_count,
      });
      if (!state || token !== generation) {
        assets?.cleanup?.();
        return;
      }
      releaseAssets();
      assetCleanup = cleanupCallback(assets?.cleanup);
      state.evidence = Array.isArray(assets?.items) ? assets.items : [];
      state.evidenceLoading = false;
      render();
      finishInitialSectionPosition();
    } catch {
      if (!state || token !== generation) return;
      state.evidenceLoading = false;
      state.evidenceError = true;
      render();
      finishInitialSectionPosition();
    }
  }

  async function beginEditing() {
    if (!state?.canEditFields || state.editSaving) return;
    state.editMode = true;
    state.editError = '';
    const needsDrivers = containerTaskModalEditableColumns(columns, role)
      .some(({ editor }) => editor === 'driver');
    state.editLoading = needsDrivers && typeof loadDriverOptions === 'function';
    render();
    if (!state.editLoading) return;
    const token = generation;
    try {
      const options = await loadDriverOptions();
      if (!state || token !== generation) return;
      state.driverOptions = normalizeDashboardDrivers(options);
      state.editLoading = false;
      render();
    } catch {
      if (!state || token !== generation) return;
      state.editLoading = false;
      state.editError = 'Available Drivers could not be loaded. Try again.';
      render();
    }
  }

  async function saveEdits(form) {
    if (!state?.canEditFields || state.editSaving || typeof onSaveField !== 'function') return;
    let requests;
    try {
      requests = editRequestsFromForm(
        form,
        containerTaskModalEditableColumns(columns, role),
      );
    } catch (error) {
      state.editError = error?.message || 'Enter valid values before saving.';
      render();
      return;
    }
    if (!requests.length) {
      state.editMode = false;
      state.editError = '';
      render();
      return;
    }
    state.editSaving = true;
    state.editError = '';
    render();
    const token = generation;
    try {
      for (const request of requests) {
        await onSaveField({
          containerId: state.row.id,
          field: request.field,
          value: request.value,
        });
      }
      if (!state || token !== generation) return;
      state.editSaving = false;
      state.editMode = false;
      await refreshDetails(token);
    } catch (error) {
      if (!state || token !== generation) return;
      state.editSaving = false;
      state.editError = error?.message || 'The authorized information could not be saved.';
      render();
    }
  }

  async function beginPodEditing() {
    if (!state?.podEditable || typeof mountPodEditor !== 'function') return;
    releaseEditor();
    state.podEditing = true;
    render();
    scrollToSection('pod');
    const host = dialog?.querySelector('[data-task-modal-pod-editor-host]');
    if (!host) return;
    try {
      const mountedEditor = await mountPodEditor({
        containerId: state.row.id,
        row: state.row,
        host,
        async onComplete() {
          if (!state) return;
          releaseEditor();
          state.podEditing = false;
          await refreshEvidence();
          scrollToSection('pod');
        },
      });
      if (!state || !dialog) {
        cleanupCallback(mountedEditor)();
        return;
      }
      editorCleanup = cleanupCallback(mountedEditor);
    } catch (error) {
      if (host) {
        host.innerHTML = `<p class="container-task-modal__empty is-error" role="alert">${escapeHtml(error?.message || 'POD editor could not be opened.')}</p>`;
      }
    }
  }

  function bindDriverProfileLayer(layer) {
    layer.addEventListener('click', (event) => {
      if (event.target === layer || event.target.closest('[data-team-driver-close]')) {
        closeDriverProfile();
        return;
      }
      const editToggle = event.target.closest('[data-team-driver-edit-toggle]');
      if (editToggle) {
        const form = layer.querySelector('[data-team-driver-edit-form]');
        if (!form) return;
        const opening = form.hidden;
        form.hidden = !opening;
        editToggle.setAttribute('aria-expanded', opening ? 'true' : 'false');
        if (opening) form.querySelector('input')?.focus?.();
        return;
      }
      if (event.target.closest('[data-team-driver-edit-cancel]')) {
        const form = layer.querySelector('[data-team-driver-edit-form]');
        const toggle = layer.querySelector('[data-team-driver-edit-toggle]');
        form?.reset?.();
        if (form) form.hidden = true;
        toggle?.setAttribute('aria-expanded', 'false');
      }
    });
    layer.addEventListener('submit', async (event) => {
      event.preventDefault();
      const form = event.target.closest('[data-team-driver-edit-form]');
      if (!form || typeof onDriverUpdate !== 'function' || !form.reportValidity()) return;
      const submit = form.querySelector('[type="submit"]');
      const errorHost = form.querySelector('[data-team-driver-edit-error]');
      if (submit) submit.disabled = true;
      if (errorHost) errorHost.textContent = '';
      try {
        await onDriverUpdate({
          driverId: form.dataset.driverId,
          values: Object.fromEntries(new FormData(form).entries()),
        });
        closeDriverProfile();
        await refreshDetails();
      } catch (error) {
        if (errorHost) errorHost.textContent = error?.message || 'Driver update failed.';
        if (submit) submit.disabled = false;
      }
    });
  }

  async function openDriverProfile(button) {
    if (!state?.driverProfilesEnabled || !dialog
      || typeof loadDriverProfile !== 'function' || typeof renderDriverProfile !== 'function') return;
    closeDriverProfile();
    const token = profileGeneration;
    const layer = documentRef.createElement('div');
    layer.className = 'team-driver-drawer-layer container-task-modal__driver-layer';
    layer.dataset.teamDriverLayer = '';
    layer.innerHTML = '<div class="container-task-modal__profile-loading" role="status">Loading Driver Profile…</div>';
    profileLayer = layer;
    dialog.append(layer);
    try {
      const profile = await loadDriverProfile(button.dataset.taskDriverOpen);
      if (!profileLayer || layer !== profileLayer || !layer.isConnected
        || token !== profileGeneration) return;
      const markup = renderDriverProfile(profile);
      if (!markup) throw new Error('Driver Profile is unavailable.');
      layer.innerHTML = markup;
      bindDriverProfileLayer(layer);
      layer.querySelector('[data-team-driver-close]')?.focus?.();
    } catch (error) {
      if (!profileLayer || layer !== profileLayer || !layer.isConnected
        || token !== profileGeneration) return;
      layer.innerHTML = `<div class="container-task-modal__profile-loading is-error" role="alert">${escapeHtml(error?.message || 'Driver Profile could not be loaded.')}</div>`;
      layer.addEventListener('click', (event) => {
        if (event.target === layer) closeDriverProfile();
      }, { once: true });
    }
  }

  function bind() {
    dialog?.querySelector('[data-task-modal-do]')
      ?.addEventListener('click', (event) => {
        void openDeliveryOrder?.(event.currentTarget.dataset.taskModalDo);
      });
    dialog?.querySelector('[data-task-modal-edit-toggle]')
      ?.addEventListener('click', () => {
        if (state.editMode) {
          state.editMode = false;
          state.editError = '';
          render();
        } else {
          void beginEditing();
        }
      });
    dialog?.querySelector('[data-task-modal-edit-cancel]')
      ?.addEventListener('click', () => {
        state.editMode = false;
        state.editError = '';
        render();
      });
    dialog?.querySelector('[data-task-modal-edit-form]')
      ?.addEventListener('submit', (event) => {
        event.preventDefault();
        void saveEdits(event.currentTarget);
      });
    dialog?.querySelectorAll('[data-task-modal-address-select]').forEach((select) => {
      select.addEventListener('change', () => {
        const field = select.dataset.taskModalAddressSelect;
        const input = dialog?.querySelector(`[data-task-modal-field="${field}"]`);
        if (!input) return;
        if (select.value === CUSTOM_ADDRESS_VALUE) {
          input.value = '';
          input.focus?.();
          return;
        }
        input.value = select.value;
      });
    });
    dialog?.querySelectorAll('[data-task-driver-open]').forEach((button) => {
      // The avatar now sits inside the leg's own summary, so the press has to be
      // stopped from also toggling the disclosure it is standing in.
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        void openDriverProfile(button);
      });
    });
    // One field at a time, on change, through the same callable the table cells
    // use - not through the edit form in the other card. That form is a batch
    // behind a Save button; two editors on one Container racing each other is how
    // a REVISION_CONFLICT reaches an operator who only typed once.
    for (const control of dialog?.querySelectorAll('[data-dispatch-field]') ?? []) {
      control.addEventListener('change', async () => {
        if (typeof onSaveField !== 'function' || !state?.row?.id) return;
        const key = control.dataset.dispatchField;
        const column = columnMap(columns).get(key);
        const errorNode = dialog?.querySelector('[data-dispatch-error]');
        if (!column) return;
        if (errorNode) errorNode.textContent = '';
        control.disabled = true;
        try {
          await onSaveField({
            containerId: state.row.id,
            field: key,
            value: dashboardEditorValue(column, control.value),
          });
          state.row = { ...state.row, [key]: control.value };
          render();
        } catch (error) {
          control.disabled = false;
          if (errorNode) {
            errorNode.textContent = error?.details?.message || error?.message
              || 'That field could not be saved.';
          }
        }
      });
    }
    dialog?.querySelector('[data-task-cancel-dispatch]')?.addEventListener('click', async (event) => {
      if (typeof onCancelDispatch !== 'function' || !state?.row?.id) return;
      const button = event.currentTarget;
      const errorNode = dialog?.querySelector('[data-task-release-error]');
      button.disabled = true;
      if (errorNode) errorNode.textContent = '';
      try {
        const result = await onCancelDispatch({ containerId: state.row.id });
        // Declining at the reason picker is not a failure, and must not leave the
        // button dead - it is the most likely outcome of pressing it by mistake,
        // which is the press this control most needs to survive.
        if (!result?.cancelled) {
          button.disabled = false;
          return;
        }
        // The work is back in Dispatch's hands: the flag, the timestamp and every
        // released leg go, so the panel offers Release again rather than claiming
        // a dispatch that no longer exists.
        state.row = {
          ...state.row,
          dispatch_released: false,
          dispatch_released_at: null,
          dispatch_released_legs: [],
        };
        render();
      } catch (error) {
        button.disabled = false;
        if (errorNode) {
          errorNode.textContent = error?.details?.message || error?.message
            || 'The dispatch could not be cancelled.';
        }
      }
    });
    dialog?.querySelector('[data-task-release]')?.addEventListener('click', async (event) => {
      // state.item never existed. Every other reference in this module is
      // state.row, so the guard below was always false and the handler returned
      // without doing anything or saying anything - Release to Drivers was a
      // button that swallowed the click. This is the whole bug.
      if (typeof onReleaseDispatch !== 'function' || !state?.row?.id) return;
      const button = event.currentTarget;
      const errorNode = dialog?.querySelector('[data-task-release-error]');
      button.disabled = true;
      if (errorNode) errorNode.textContent = '';
      try {
        const result = await onReleaseDispatch({ containerId: state.row.id });
        // Trust the server's answer rather than assuming: the release is
        // idempotent, so a second press has to show the first press's timestamp.
        // allLegs is everything sent across every press - patching in only this
        // press's legs would make the panel forget the pickup the moment the
        // empty return followed it out.
        state.row = {
          ...state.row,
          dispatch_released: true,
          dispatch_released_at: Number.isSafeInteger(result?.releasedAtMillis)
            ? new Date(result.releasedAtMillis) : new Date(),
          dispatch_released_legs: Array.isArray(result?.allLegs)
            ? result.allLegs
            : Array.isArray(result?.legs) ? result.legs : [],
        };
        render();
      } catch (error) {
        button.disabled = false;
        if (errorNode) {
          errorNode.textContent = error?.details?.message || error?.message
            || 'The Task could not be released.';
        }
      }
    });
    dialog?.querySelector('[data-task-modal-manual-empty-return]')?.addEventListener('click', async (event) => {
      if (typeof onManuallyCompleteEmptyReturn !== 'function' || !state?.row?.id) return;
      const button = event.currentTarget;
      const errorNode = dialog?.querySelector('[data-task-modal-manual-empty-return-error]');
      button.disabled = true;
      if (errorNode) errorNode.textContent = '';
      try {
        await onManuallyCompleteEmptyReturn({ containerId: state.row.id });
        // Trust the server's canonical fields rather than assuming every one of
        // them - the same reason onReleaseDispatch's handler reads its response
        // instead of hardcoding what a press ought to have done.
        state.row = {
          ...state.row,
          status: 'empty_returned',
          is_empty_returned: true,
          locked: true,
          current_action_type: 'finance_completion',
        };
        render();
      } catch (error) {
        button.disabled = false;
        if (errorNode) {
          errorNode.textContent = error?.details?.message || error?.message
            || 'Empty Return could not be marked complete.';
        }
      }
    });
    dialog?.querySelector('[data-task-modal-manage-pod]')
      ?.addEventListener('click', () => { void beginPodEditing(); });
    dialog?.querySelector('[data-task-modal-cancel-pod]')
      ?.addEventListener('click', () => {
        releaseEditor();
        state.podEditing = false;
        render();
        scrollToSection('pod');
      });
  }

  function scrollToSection(sectionKey) {
    if (!dialog) return;
    const run = () => {
      const body = dialog?.querySelector('.container-task-modal__body');
      const section = dialog?.querySelector(`[data-task-modal-section="${sectionKey}"]`);
      if (!body || !section) return;
      const bodyBounds = body.getBoundingClientRect();
      const sectionBounds = section.getBoundingClientRect();
      body.scrollTop += sectionBounds.top - bodyBounds.top - 12;
    };
    const schedule = documentRef.defaultView?.requestAnimationFrame?.bind(documentRef.defaultView)
      || globalThis.requestAnimationFrame?.bind(globalThis)
      || ((callback) => setTimeout(callback, 0));
    schedule(run);
  }

  function finishInitialSectionPosition() {
    if (!state || state.detailsLoading || state.evidenceLoading) return;
    const sectionKey = state.initialSection;
    state.initialSection = 'overview';
    if (sectionKey && sectionKey !== 'overview') scrollToSection(sectionKey);
  }

  function render({ preserveScroll = true } = {}) {
    if (!dialog || !state) return;
    const previousScrollTop = preserveScroll
      ? dialog.querySelector('.container-task-modal__body')?.scrollTop || 0
      : 0;
    dialog.innerHTML = containerTaskModalMarkup({
      ...state,
      role,
      columns,
      formatValue,
      // Value or getter (2026-08-11). The catalog costs about twenty callables -
      // one per customer - and the dashboard used to await all of them before it
      // painted. Resolved here, at the moment a modal actually opens, so first
      // paint never waits for it.
      addressCatalog: typeof addressCatalog === 'function' ? addressCatalog() : addressCatalog,
      deliveryOrderOpenable: typeof openDeliveryOrder === 'function',
    });
    bind();
    const body = dialog.querySelector('.container-task-modal__body');
    if (body) body.scrollTop = previousScrollTop;
  }

  async function open({
    row,
    initialSection = 'overview',
    podEditable = false,
    pendingEmptyReturnReview = false,
    anchor = null,
  } = {}) {
    if (!row || typeof row.id !== 'string' || !row.id.trim()) return false;
    close({ restoreFocus: false });
    const token = generation;
    const requestedSection = containerTaskModalSections(role)
      .some(({ key }) => key === initialSection) ? initialSection : 'overview';
    trigger = anchor;
    state = {
      row,
      detail: null,
      related: null,
      evidence: [],
      podEditable: podEditable === true,
      podEditing: false,
      pendingEmptyReturnReview: pendingEmptyReturnReview === true,
      detailsLoading: true,
      detailsError: false,
      evidenceLoading: true,
      evidenceError: false,
      canEditFields: typeof onSaveField === 'function'
        && containerTaskModalEditableColumns(columns, role).length > 0,
      editMode: false,
      editLoading: false,
      editSaving: false,
      editError: '',
      driverOptions: [],
      driverProfilesEnabled: FINANCE_ROLES.has(roleToken(role))
        && typeof loadDriverProfile === 'function'
        && typeof renderDriverProfile === 'function',
      initialSection: requestedSection,
    };
    dialog = documentRef.createElement('dialog');
    dialog.className = 'container-task-modal';
    dialog.setAttribute('aria-labelledby', 'containerTaskModalTitle');
    dialog.setAttribute('tabindex', '-1');
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      close();
    });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) close();
    });
    documentRef.body.append(dialog);
    render({ preserveScroll: false });
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    dialog.focus?.({ preventScroll: true });
    if (requestedSection !== 'overview') scrollToSection(requestedSection);

    void refreshEvidence(token);
    await refreshDetails(token);
    return true;
  }

  return Object.freeze({
    open,
    close,
    isOpen: () => dialog !== null,
    // Re-reads the detail and the evidence for whatever Task is on screen, and
    // does nothing when nothing is. Called when a driver's progress fires the
    // live refresh signal, so an open Task follows the driver instead of showing
    // the state it had when it was opened (Alan, 2026-08-03).
    //
    // Deliberately does NOT touch state.row: the row comes from the Dashboard
    // table, which refreshes itself on the same signal and then re-renders. Two
    // writers for one field is how they end up disagreeing.
    async refreshOpenTask() {
      if (!dialog || !state?.row?.id) return false;
      const token = generation;
      state.detailsLoading = true;
      state.evidenceLoading = true;
      render({ preserveScroll: true });
      void refreshEvidence(token);
      await refreshDetails(token);
      return true;
    },
    destroy: () => close({ restoreFocus: false }),
  });
}
